/**
 * How a product row reads — the vocabulary, the language pick, and the split the bulk bar
 * restates.
 *
 * Pure, and separate from the screen for the reason this project keeps finding: the parts
 * of a table that can be *wrong* are decisions about values, and a decision about a value
 * should not need a browser to check. Three of the four functions below encode a rule that
 * is written down somewhere and would otherwise live only in JSX.
 */
import type { ProductStatus } from "@packagepage/shared";

/**
 * The words each catalogue status renders in — **the shopper's, where the shopper has
 * one.**
 *
 * `components.md`: *"the terminal product status reads **'No longer available'** — the same
 * words a shopper sees. One vocabulary across both audiences. 'Tombstone' and 'delete'
 * never appear in the UI."*
 *
 * The database column says `tombstoned` and this map is the only place that word is
 * allowed to be read. Note what the rule actually forbids: not the *concept*, which the API
 * and the schema need, but the **rendering** — so the check that keeps this honest asserts
 * on rendered output, not on this file's source, and the two are different claims.
 */
export const STATUS_LABEL: Readonly<Record<ProductStatus, string>> = {
  draft: "Draft",
  published: "Published",
  archived: "Archived",
  tombstoned: "No longer available",
};

/**
 * The `Pill` family value each status maps to.
 *
 * `PILL_TONES.product` is keyed `published | draft | archived | unavailable`, and the
 * schema's fourth status is `tombstoned` — the vocabulary boundary, crossed here once
 * rather than at every call site. `packages/ui` chose the shopper's word for the key
 * deliberately; this is where the storage word stops.
 */
export const STATUS_PILL: Readonly<
  Record<ProductStatus, "published" | "draft" | "archived" | "unavailable">
> = {
  draft: "draft",
  published: "published",
  archived: "archived",
  tombstoned: "unavailable",
};

/**
 * Why a row cannot be selected, or `undefined` when it can.
 *
 * Returned as a **sentence**, because `DataTable` renders it in the primary cell where the
 * eye already is — `components.md`'s *"ineligible rows stay visible, unselectable, with the
 * reason in the primary cell"*. A boolean here would throw away the only part the user
 * needs, and hiding the row answers *"why is my product missing"* with silence.
 *
 * Only the terminal state is ineligible. An archived product is ordinary and reversible;
 * a retired one is not, and it is the row `screens.md` §3 names.
 */
export function ineligibleReason(status: ProductStatus): string | undefined {
  return status === "tombstoned"
    ? "Retired permanently — its code answers with a notice and nothing about it can change."
    : undefined;
}

export interface DisplayName {
  readonly text: string;
  /**
   * The language the text was taken from, when it is **not** English.
   *
   * Surfaced rather than swallowed: a catalogue named in German rendered silently in
   * German looks like the product has no English name *and* like the screen is confused.
   * `null` means English, or means there is nothing to say.
   */
  readonly lang: string | null;
}

/**
 * One string out of the jsonb language map, and which language it came from.
 *
 * `products.name` is a map of language tag to string, and the list has to render one line
 * per row. **This is not the per-field language switching the detail screen owes** — that
 * is a control at the field, and this is a fallback chain for a 47px row that has space for
 * one language and no control at all.
 *
 * `unknown` in, because `productSummarySchema` deliberately types it that way: a schema
 * that picked a language would be making this decision in the wrong place, and this
 * function is the right place precisely because it can say which language it picked.
 */
export function displayName(name: unknown, gtin14: string): DisplayName {
  if (typeof name !== "object" || name === null) return { text: gtin14, lang: null };

  const map = name as Record<string, unknown>;
  const english = map["en"];
  if (typeof english === "string" && english !== "") return { text: english, lang: null };

  for (const [lang, value] of Object.entries(map)) {
    if (typeof value === "string" && value !== "") return { text: value, lang };
  }

  // A product with no name at all is a real state — `POST /v1/products` makes `name`
  // optional — and its GTIN is the only identity it has. Rendering an empty cell would
  // make the row look broken rather than incomplete.
  return { text: gtin14, lang: null };
}

export interface PublishSplit {
  /** Rows loaded on this screen that the action would publish. */
  readonly publishable: readonly string[];
  /** Loaded and selected, but already published — the action skips them. */
  readonly alreadyPublished: number;
  /** Loaded and selected, but retired or archived — the action cannot touch them. */
  readonly ineligible: number;
}

/**
 * The split a bulk action restates **before** it runs.
 *
 * `components.md`: *"Bulk actions restate the split before running."* The split is over
 * the rows this screen has **loaded**, and that is the honest bound rather than a
 * limitation to hide: a filter selection can name nine hundred rows while a hundred are in
 * memory, and there is no bulk endpoint behind this screen — §5 specifies
 * `PATCH /products:batch (≤500)` and nothing has built it. So the bar states the
 * authoritative count from the server *and* what the button in front of the user will
 * actually do, and the gap between the two numbers is shown rather than averaged away.
 */
export function publishSplit(
  rows: readonly { id: string; status: ProductStatus }[],
  isSelected: (id: string) => boolean,
): PublishSplit {
  const publishable: string[] = [];
  let alreadyPublished = 0;
  let ineligible = 0;

  for (const row of rows) {
    if (!isSelected(row.id)) continue;
    if (row.status === "draft") publishable.push(row.id);
    else if (row.status === "published") alreadyPublished += 1;
    else ineligible += 1;
  }

  return { publishable, alreadyPublished, ineligible };
}
